import { Controller } from "@hotwired/stimulus"
import { getValidatedSearchFormState } from '../utils/dateStorage'

export default class extends Controller {
  static targets = ["guestButton", "dropdown", "summary", "childAgeSection", "childAgeInputs"]
  adultIcon = `<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 9.016a2.964 2.964 0 1 0 0-5.928 2.964 2.964 0 0 0 0 5.928" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M13.072 14.088a5.072 5.072 0 1 0-10.144 0" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  childIcon = `<svg width="16" height="17" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="var(--ci-primary-color, #000000)" d="M425.39 200.035A184.3 184.3 0 0 0 290.812 91.289l26.756-42.809-27.136-16.96-35.305 56.488A184.05 184.05 0 0 0 86.61 200.035a71.978 71.978 0 0 0 0 143.93 184.071 184.071 0 0 0 338.78 0 71.978 71.978 0 0 0 0-143.93m27.152 99.975a39.77 39.77 0 0 1-27.76 11.961l-20.725.394-8.113 19.074a152.066 152.066 0 0 1-279.887 0l-8.114-19.074-20.725-.394a39.978 39.978 0 0 1 0-79.942l20.725-.394 8.114-19.074a152.067 152.067 0 0 1 279.887 0l8.113 19.074 20.725.394a39.974 39.974 0 0 1 27.76 67.981" class="ci-primary"/><path fill="var(--ci-primary-color, #000000)" class="ci-primary" d="M168 232h40v40h-40zm136 0h40v40h-40zm-48 152a80 80 0 0 0 80-80H176a80 80 0 0 0 80 80"/></svg>`

  initialize() {
    const params = new URLSearchParams(window.location.search);
    // Get values from session storage
    const storedDates = getValidatedSearchFormState();
    
    // Use URL params first, then fall back to session storage
    this.adultsCount = params.get("adults") || storedDates.adultCount || 2;
    this.childrenCount = params.get("children") || storedDates.childrenCount || 0;
    this.roomsCount = params.get("rooms") || storedDates.roomsCount || 1;
    
    // For child ages, first try URL params, then session storage
    const urlChildAges = this.getChildAges(params);
    this.childAges = urlChildAges.length > 0 ? urlChildAges : storedDates.childrenAges || [];
  }

  connect() {
    this.checkGuestQueries()
    
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this))
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick.bind(this))
  }

  handleOutsideClick(event) {
    if (this.dropdownTarget.classList.contains('hidden')) return

    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  toggleDropdown() {
    if (this.dropdownTarget.classList.contains('hidden')) {
      this.showDropdown()
    } else {
      this.hideDropdown()
    }
  }

  showDropdown() {
    this.dropdownTarget.classList.remove('hidden')
    this.guestButtonTarget.setAttribute('aria-expanded', 'true')
  }

  hideDropdown() {
    this.dropdownTarget.classList.add('hidden')
    this.guestButtonTarget.setAttribute('aria-expanded', 'false')
  }

  incrementAdults(event) {
    // Prevent form submission
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (this.adultsCount < 30) {
      this.adultsCount++
      document.getElementById('adults-count').textContent = this.adultsCount
      document.getElementById('adults-count').dataset.value = this.adultsCount
      this.updateSummary()
      this.updateHiddenInputs()
    }
  }

  decrementAdults(event) {
    // Prevent form submission
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (this.adultsCount > 1) {
      this.adultsCount--
      document.getElementById('adults-count').textContent = this.adultsCount
      document.getElementById('adults-count').dataset.value = this.adultsCount
      this.updateSummary()
      this.updateHiddenInputs()
    }
  }

  incrementChildren(event) {
    // Prevent form submission
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (this.childrenCount < 10) {
      this.childrenCount++
      document.getElementById('children-count').textContent = this.childrenCount
      document.getElementById('children-count').dataset.value = this.childrenCount
      this.updateSummary()
      this.updateChildAgeInputs()
      this.updateHiddenInputs()
    }
  }

  decrementChildren(event) {
    // Prevent form submission
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (this.childrenCount > 0) {
      this.childrenCount--
      document.getElementById('children-count').textContent = this.childrenCount
      document.getElementById('children-count').dataset.value = this.childrenCount
      this.updateSummary()
      this.updateChildAgeInputs()
      this.updateHiddenInputs()
    }
  }

  incrementRooms(event) {
    // Prevent form submission
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (this.roomsCount < 10) {
      this.roomsCount++
      document.getElementById('rooms-count').textContent = this.roomsCount
      document.getElementById('rooms-count').dataset.value = this.roomsCount
      this.updateSummary()
      this.updateHiddenInputs()
    }
  }

  decrementRooms(event) {
    // Prevent form submission
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (this.roomsCount > 1) {
      this.roomsCount--
      document.getElementById('rooms-count').textContent = this.roomsCount
      document.getElementById('rooms-count').dataset.value = this.roomsCount
      this.updateSummary()
      this.updateHiddenInputs()
    }
  }

  updateSummary() {
    const isMobile = window.innerWidth < 768;
    const adultText = this.adultsCount === 1 ? 'Adult' : 'Adults'
    const childText = this.childrenCount === 1 ? 'Child' : 'Children'
    const roomText = this.roomsCount === 1 ? 'Room' : 'Rooms'

    let adultsContent = `${this.adultsCount} ${adultText}`
    let childrenContent = `${this.childrenCount} ${childText}`
    
    if (isMobile && !this.ishomepage()) {
      adultsContent = `${this.adultsCount} ${this.adultIcon}`
      childrenContent = `${this.childrenCount} ${this.childIcon}`
    }
    this.summaryTarget.innerHTML = `${adultsContent} <span class="px-0 text-xs md:text-2xl align-middle md:align-top leading-normal">◆</span> ${childrenContent} <span class="px-0 text-xs md:text-2xl align-middle md:align-top leading-normal">◆</span> ${this.roomsCount} ${roomText}`
  }

  updateChildAgeInputs() {
    // Show/hide child age section based on children count
    if (this.childrenCount > 0) {
      this.childAgeSectionTarget.classList.remove('hidden')
    } else {
      this.childAgeSectionTarget.classList.add('hidden')
      return
    }

    // Clear existing inputs
    this.childAgeInputsTarget.innerHTML = ''

    // Add age input for each child
    for (let i = 0; i < this.childrenCount; i++) {
      const childNumber = i + 1
      const ageInputRow = document.createElement('div')
      ageInputRow.className = 'flex justify-between items-center'
      ageInputRow.innerHTML = `
        <p class="text-black">Age of Child ${childNumber}</p>
        <div class="relative w-40">
          <select
            id="child-age-${i}"
            class="w-full appearance-none bg-white border border-gray-300 rounded-md py-2 px-4 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-child-index="${i}"
            data-action="change->search-form#disableSearchButton"
          >
            ${this.generateAgeOptions(this.childAges[i] || 0)}
          </select>
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-black">
            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
      `
      this.childAgeInputsTarget.appendChild(ageInputRow)
    }
  }

  generateAgeOptions(selectedValue) {
    let options = ''
    // Add options from 0 to 17
    for (let age = 0; age <= 17; age++) {
      options += `<option value="${age}" ${age === selectedValue ? "selected" : ""}>${age}</option>`
    }
    return options
  }

  updateHiddenInputs() {
    // Update hidden inputs for form submission
    const adultsInput = document.getElementById('adults-input')
    const childrenInput = document.getElementById('children-input')
    const roomsInput = document.getElementById('rooms-input')

    if (adultsInput) adultsInput.value = this.adultsCount
    if (childrenInput) childrenInput.value = this.childrenCount
    if (roomsInput) roomsInput.value = this.roomsCount

    // Handle child age inputs
    if (this.childrenCount > 0) {
      // Remove any existing child age hidden inputs
      document.querySelectorAll('input[name^="child_age"]').forEach(input => input.remove())

      // Add new hidden inputs for each child's age
      const childAgeSelects = document.querySelectorAll('[id^="child-age-"]')
      const form = document.getElementById('search-form')

      if (!form) return

      childAgeSelects.forEach((select, index) => {
        const hiddenInput = document.createElement('input')
        hiddenInput.type = 'hidden'
        hiddenInput.name = `child_age[${index}]`
        hiddenInput.value = select.value

        // Add event listener to update hidden input when select changes
        // Use a safer way to add event listeners that won't trigger form submission
        const safeUpdateValue = function(e) {
          // Update the hidden input value without submitting the form
          hiddenInput.value = this.value
        }

        // Add the event listener
        select.addEventListener('change', safeUpdateValue)

        // Append to form
        form.appendChild(hiddenInput)
      })
    }
  }

  checkGuestQueries() {
    const adultsCount = document.getElementById('adults-count')
    const childrenCount = document.getElementById('children-count')
    const roomsCount = document.getElementById('rooms-count')

    if(adultsCount) {
      adultsCount.textContent = this.adultsCount
      adultsCount.dataset.value = this.adultsCount
    }

    if(childrenCount) {
      childrenCount.textContent = this.childrenCount
      childrenCount.dataset.value = this.childrenCount
    }

    if(roomsCount) {
      roomsCount.textContent = this.roomsCount
      roomsCount.dataset.value = this.roomsCount
    }
    
    this.updateHiddenInputs()
    this.updateChildAgeInputs()
    this.updateSummary()
  }

  getChildAges(params) {
    const childAges = [];
    params.forEach((value, key) => {
      if (key.startsWith('child_age[')) {
        childAges.push(Number(value)); // Convert to number if needed
      } 
    });

    return childAges;
  }

  ishomepage() {
    return document.getElementById("search-form").classList.contains("bg-black");
  }
}
